<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -- SiteUP-only schedule screen uses product copy directly until its locale catalog is introduced */
// SiteUp (Onda C): "Agendas" do Captain — CRUD das Captain::AssistantSchedule.
// Cada agenda amarra um FUNIL a uma grade de horários (business_hours por dia),
// duração do slot, timezone e bloqueio de double-booking. A tool
// schedule_appointment do agente escolhe a agenda pela `when_to_use` e valida o
// horário pedido contra a grade. O Google Calendar é conectado por FUNIL, então
// só mostramos o status (read-only) aqui. Tela auto-contida: consome o API
// client direto (sem store Vuex novo), só reusa o store de funis pro dropdown.
import { computed, ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';
import AssistantSchedulesAPI from 'dashboard/api/captain/assistantSchedules';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import PageLayout from 'dashboard/components-next/captain/PageLayout.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';

const route = useRoute();
const store = useStore();
const { isAdmin } = useAdmin();

const assistantId = computed(() => Number(route.params.assistantId));
const canEdit = computed(() => isAdmin.value);

const funnels = useMapGetter('funnel/getFunnels');
const labels = useMapGetter('labels/getLabels');

const schedules = ref([]);
const isFetching = ref(false);

// Estado do formulário (cria/edita uma agenda por vez via dialog inline).
const isFormOpen = ref(false);
const isSaving = ref(false);
const editingId = ref(null);

// Confirmação de exclusão.
const deletingId = ref(null);
const isDeleting = ref(false);

// Ordem alinhada ao backend (Captain::AssistantSchedule::DAY_KEYS, 0 = domingo).
const DAYS = [
  { key: 'sunday', label: 'Domingo' },
  { key: 'monday', label: 'Segunda' },
  { key: 'tuesday', label: 'Terça' },
  { key: 'wednesday', label: 'Quarta' },
  { key: 'thursday', label: 'Quinta' },
  { key: 'friday', label: 'Sexta' },
  { key: 'saturday', label: 'Sábado' },
];
const DAY_SHORT = {
  sunday: 'Dom',
  monday: 'Seg',
  tuesday: 'Ter',
  wednesday: 'Qua',
  thursday: 'Qui',
  friday: 'Sex',
  saturday: 'Sáb',
};

// Fusos do Brasil (mesma lista do agendamento legado), com rótulo amigável —
// evita o cliente digitar fuso errado em campo de texto livre.
const TIMEZONES = [
  { value: 'America/Sao_Paulo', label: 'Brasília / São Paulo (GMT-3)' },
  { value: 'America/Fortaleza', label: 'Fortaleza / Nordeste (GMT-3)' },
  { value: 'America/Manaus', label: 'Manaus / Cuiabá (GMT-4)' },
  { value: 'America/Rio_Branco', label: 'Rio Branco / Acre (GMT-5)' },
  { value: 'America/Noronha', label: 'Fernando de Noronha (GMT-2)' },
];

const funnelOptions = computed(() =>
  (funnels.value || []).map(f => ({ value: f.id, label: f.name }))
);

const labelOptions = computed(() =>
  (labels.value || []).map(l => ({ value: l.title, label: l.title }))
);

// Normaliza um dia: prefer windows[]; se ausente, monta 1 janela a partir de start/end legados.
const normalizeDayWindows = cfg => {
  const raw = Array.isArray(cfg?.windows) ? cfg.windows : [];
  const fromWindows = raw
    .filter(w => w && (w.start || w.end))
    .map(w => ({
      start: w.start || '09:00',
      end: w.end || '18:00',
    }));
  if (fromWindows.length) return fromWindows;
  return [
    {
      start: cfg?.start || '09:00',
      end: cfg?.end || '18:00',
    },
  ];
};

const buildBusinessHours = source => {
  const out = {};
  DAYS.forEach(({ key }) => {
    const cfg = source?.[key] || {};
    // Dias úteis ligados por padrão, fim de semana desligado.
    const defaultEnabled = key !== 'sunday' && key !== 'saturday';
    const windows = normalizeDayWindows(cfg);
    out[key] = {
      enabled: cfg.enabled ?? defaultEnabled,
      // start/end espelham a 1ª/última janela (compat legado + inputs simples).
      start: windows[0].start,
      end: windows[windows.length - 1].end,
      windows,
    };
  });
  return out;
};

const blankForm = () => ({
  name: '',
  funnel_id: '',
  when_to_use: '',
  timezone: 'America/Sao_Paulo',
  slot_duration_minutes: 30,
  // vazio = grade igual à duração (compat com agendas antigas)
  slot_interval_minutes: '',
  enabled: true,
  block_double_booking: true,
  label: '',
  business_hours: buildBusinessHours(),
});

const form = ref(blankForm());

// Resumo legível dos horários habilitados, ex.: "Seg 09:00-12:00 / 14:00-18:00".
const summarizeHours = businessHours => {
  const parts = DAYS.filter(({ key }) => businessHours?.[key]?.enabled).map(
    ({ key }) => {
      const day = businessHours[key];
      const windows = normalizeDayWindows(day);
      const ranges = windows.map(w => `${w.start}-${w.end}`).join(' / ');
      return `${DAY_SHORT[key]} ${ranges}`;
    }
  );
  return parts.length ? parts.join(', ') : 'Nenhum dia habilitado';
};

const fetchSchedules = async () => {
  isFetching.value = true;
  try {
    const { data } = await AssistantSchedulesAPI.get({
      assistantId: assistantId.value,
    });
    schedules.value = data || [];
  } catch {
    useAlert('Não foi possível carregar as agendas.');
  } finally {
    isFetching.value = false;
  }
};

const openCreate = () => {
  editingId.value = null;
  form.value = blankForm();
  isFormOpen.value = true;
};

const openEdit = schedule => {
  editingId.value = schedule.id;
  form.value = {
    name: schedule.name || '',
    funnel_id: schedule.funnel_id || '',
    when_to_use: schedule.when_to_use || '',
    timezone: schedule.timezone || 'America/Sao_Paulo',
    slot_duration_minutes: schedule.slot_duration_minutes || 30,
    slot_interval_minutes:
      schedule.slot_interval_minutes == null
        ? ''
        : schedule.slot_interval_minutes,
    enabled: schedule.enabled ?? true,
    block_double_booking: schedule.block_double_booking ?? true,
    label: schedule.label || '',
    business_hours: buildBusinessHours(schedule.business_hours),
  };
  isFormOpen.value = true;
};

const closeForm = () => {
  isFormOpen.value = false;
  editingId.value = null;
};

const payloadFor = data => {
  const business_hours = {};
  DAYS.forEach(({ key }) => {
    const day = data.business_hours?.[key] || {};
    const windows = normalizeDayWindows(day)
      .map(w => ({ start: w.start, end: w.end }))
      .sort((a, b) => a.start.localeCompare(b.start));
    business_hours[key] = {
      enabled: !!day.enabled,
      start: windows[0]?.start || day.start || '09:00',
      end: windows[windows.length - 1]?.end || day.end || '18:00',
      windows,
    };
  });
  const intervalRaw = data.slot_interval_minutes;
  const interval =
    intervalRaw === '' || intervalRaw == null ? null : Number(intervalRaw);

  return {
    name: data.name.trim(),
    funnel_id: Number(data.funnel_id),
    when_to_use: data.when_to_use,
    timezone: data.timezone.trim(),
    slot_duration_minutes: Number(data.slot_duration_minutes),
    slot_interval_minutes:
      Number.isFinite(interval) && interval > 0 ? interval : null,
    enabled: data.enabled,
    block_double_booking: data.block_double_booking,
    label: data.label || null,
    business_hours,
  };
};

const saveSchedule = async () => {
  if (!form.value.name.trim()) {
    useAlert('Dê um nome para a agenda.');
    return;
  }
  if (!form.value.funnel_id) {
    useAlert('Escolha o funil da agenda.');
    return;
  }
  if (!form.value.timezone.trim()) {
    useAlert('Informe o fuso horário.');
    return;
  }
  const slot = Number(form.value.slot_duration_minutes);
  if (!slot || slot < 1 || slot > 480) {
    useAlert('A duração do atendimento deve ficar entre 1 e 480 minutos.');
    return;
  }
  const intervalRaw = form.value.slot_interval_minutes;
  if (intervalRaw !== '' && intervalRaw != null) {
    const interval = Number(intervalRaw);
    if (!interval || interval < 1 || interval > 480) {
      useAlert(
        'O intervalo da grade (quando informado) deve ficar entre 1 e 480 minutos.'
      );
      return;
    }
  }
  // Janelas: start < end; não sobrepostas; ordenadas. HH:MM compara lexicograficamente.
  const invalidDays = [];
  DAYS.forEach(({ key, label }) => {
    const d = form.value.business_hours[key];
    if (!d?.enabled) return;
    const windows = normalizeDayWindows(d);
    if (windows.some(w => !w.start || !w.end || w.start >= w.end)) {
      invalidDays.push(label);
      return;
    }
    const sorted = [...windows].sort((a, b) => a.start.localeCompare(b.start));
    for (let i = 1; i < sorted.length; i += 1) {
      if (sorted[i - 1].end > sorted[i].start) {
        invalidDays.push(label);
        break;
      }
    }
  });
  if (invalidDays.length) {
    useAlert(
      `Revise as janelas (início < fim, sem sobreposição) em: ${invalidDays.join(', ')}.`
    );
    return;
  }

  isSaving.value = true;
  try {
    const body = payloadFor(form.value);
    if (editingId.value) {
      const { data } = await AssistantSchedulesAPI.update({
        assistantId: assistantId.value,
        id: editingId.value,
        ...body,
      });
      const idx = schedules.value.findIndex(s => s.id === editingId.value);
      if (idx !== -1) schedules.value.splice(idx, 1, data);
      useAlert('Agenda atualizada.');
    } else {
      const { data } = await AssistantSchedulesAPI.create({
        assistantId: assistantId.value,
        ...body,
      });
      schedules.value.push(data);
      useAlert('Agenda criada.');
    }
    closeForm();
  } catch (error) {
    const apiError = error?.response?.data?.errors?.[0];
    useAlert(apiError || 'Não foi possível salvar a agenda.');
  } finally {
    isSaving.value = false;
  }
};

const confirmDelete = id => {
  deletingId.value = id;
};

const cancelDelete = () => {
  deletingId.value = null;
};

const deleteSchedule = async id => {
  isDeleting.value = true;
  try {
    await AssistantSchedulesAPI.delete({
      assistantId: assistantId.value,
      id,
    });
    schedules.value = schedules.value.filter(s => s.id !== id);
    useAlert('Agenda removida.');
    deletingId.value = null;
  } catch {
    useAlert('Não foi possível remover a agenda.');
  } finally {
    isDeleting.value = false;
  }
};

const fieldClass =
  'w-full px-3 py-2 rounded-md border border-n-weak bg-n-background text-n-slate-12 text-sm disabled:opacity-60';
const splitHoursHelp =
  'Use uma janela contínua ou adicione um segundo período (ex.: 09:00–12:00 e 14:00–18:00). O almoço fica fora da grade e não recebe slots.';
const addPeriodLabel = '+ Adicionar período';
const closedLabel = 'Fechado';

const syncDayBounds = dayKey => {
  const day = form.value.business_hours[dayKey];
  if (!day || !Array.isArray(day.windows) || !day.windows.length) return;
  day.start = day.windows[0].start;
  day.end = day.windows[day.windows.length - 1].end;
};

const addWindow = dayKey => {
  const day = form.value.business_hours[dayKey];
  if (!day) return;
  if (!Array.isArray(day.windows)) day.windows = normalizeDayWindows(day);
  const first = day.windows[0];
  if (
    day.windows.length === 1 &&
    first.start < '12:00' &&
    first.end > '14:00'
  ) {
    const originalEnd = first.end;
    first.end = '12:00';
    day.windows.push({ start: '14:00', end: originalEnd });
  } else if (first.start >= '14:00') {
    day.windows.unshift({ start: '09:00', end: '12:00' });
  } else {
    day.windows.push({ start: '14:00', end: '18:00' });
  }
  syncDayBounds(dayKey);
};

const removeWindow = (dayKey, index) => {
  const day = form.value.business_hours[dayKey];
  if (!day || !Array.isArray(day.windows) || day.windows.length <= 1) return;
  day.windows.splice(index, 1);
  day.start = day.windows[0].start;
  day.end = day.windows[day.windows.length - 1].end;
};

onMounted(async () => {
  await store.dispatch('funnel/fetch').catch(() => {});
  store.dispatch('labels/get').catch(() => {});
  await fetchSchedules();
});
</script>

<template>
  <PageLayout
    header-title="Agendas"
    :is-fetching="isFetching"
    :show-know-more="false"
    :show-pagination-footer="false"
  >
    <template #body>
      <SettingsHeader
        heading="Agendas"
        description="Defina os horários de atendimento por funil que a IA usa ao agendar compromissos. Cada agenda tem sua grade de horários, duração do slot e fuso. O agente escolhe a agenda certa pela descrição de uso e só oferece horários dentro da grade."
      />

      <div
        class="flex items-start gap-2 mt-4 px-3 py-2 rounded-xl bg-n-slate-2 text-n-slate-11 text-xs"
      >
        <span class="i-ri-information-line size-4 shrink-0 mt-0.5" />
        <span>
          A conexão com o Google Calendar é feita por funil (em Funis). Aqui
          você só vê se o funil da agenda já está conectado — quando estiver, os
          compromissos criados pela IA vão para o calendário do funil.
        </span>
      </div>

      <!-- Barra de ação -->
      <div class="flex items-center justify-between gap-3 mt-6">
        <span class="text-sm text-n-slate-11">
          {{ schedules.length }}
          {{ schedules.length === 1 ? 'agenda' : 'agendas' }}
        </span>
        <Button
          label="Nova agenda"
          icon="i-ri-add-line"
          sm
          :disabled="!canEdit"
          @click="openCreate"
        />
      </div>

      <!-- Lista -->
      <div v-if="!schedules.length && !isFormOpen" class="mt-6">
        <span class="text-sm text-n-slate-11">
          Nenhuma agenda ainda. Clique em "Nova agenda" para criar a primeira.
        </span>
      </div>

      <div class="flex flex-col gap-3 mt-4">
        <div
          v-for="schedule in schedules"
          :key="schedule.id"
          class="rounded-xl border border-n-weak p-4 flex flex-col gap-3"
        >
          <div class="flex items-start justify-between gap-3">
            <div class="flex flex-col gap-1 min-w-0">
              <div class="flex items-center gap-2 flex-wrap">
                <span class="text-sm font-medium text-n-slate-12 break-words">
                  {{ schedule.name }}
                </span>
                <span
                  v-if="schedule.enabled"
                  class="text-xs px-2 py-0.5 rounded-full bg-n-teal-3 text-n-teal-11"
                >
                  Ativa
                </span>
                <span
                  v-else
                  class="text-xs px-2 py-0.5 rounded-full bg-n-slate-3 text-n-slate-11"
                >
                  Inativa
                </span>
                <span
                  v-if="schedule.google_calendar_connected"
                  class="text-xs px-2 py-0.5 rounded-full bg-n-iris-3 text-n-iris-11 flex items-center gap-1"
                >
                  <span class="i-ri-calendar-check-line size-3" />
                  Google Calendar conectado
                </span>
              </div>
              <span class="text-xs text-n-slate-11">
                Funil: {{ schedule.funnel_name || '—' }} ·
                {{ schedule.slot_duration_minutes }} min
                <template
                  v-if="
                    schedule.slot_interval_minutes &&
                    schedule.slot_interval_minutes !==
                      schedule.slot_duration_minutes
                  "
                >
                  · grade {{ schedule.slot_interval_minutes }} min</template
                >
                · {{ schedule.timezone }}
                <template v-if="schedule.label">
                  · Etiqueta: {{ schedule.label }}</template
                >
              </span>
              <span class="text-xs text-n-slate-11">
                {{ summarizeHours(schedule.business_hours) }}
              </span>
              <span
                v-if="!schedule.google_calendar_connected"
                class="text-xs text-n-amber-11 flex items-center gap-1 mt-0.5"
              >
                <span class="i-ri-alert-line size-3 shrink-0" />
                Google Calendar não conectado neste funil — os compromissos não
                serão enviados ao calendário.
              </span>
            </div>

            <div class="flex items-center gap-2 shrink-0">
              <Button
                icon="i-ri-pencil-line"
                slate
                ghost
                sm
                :disabled="!canEdit"
                @click="openEdit(schedule)"
              />
              <Button
                icon="i-ri-delete-bin-line"
                ruby
                ghost
                sm
                :disabled="!canEdit"
                @click="confirmDelete(schedule.id)"
              />
            </div>
          </div>

          <!-- Confirmação de exclusão inline -->
          <div
            v-if="deletingId === schedule.id"
            class="flex items-center justify-between gap-3 px-3 py-2 rounded-md bg-n-ruby-2 text-n-ruby-11 text-xs flex-wrap"
          >
            <span
              >Remover a agenda "{{ schedule.name }}"? Não dá pra
              desfazer.</span
            >
            <div class="flex items-center gap-2">
              <Button
                label="Cancelar"
                slate
                ghost
                xs
                :disabled="isDeleting"
                @click="cancelDelete"
              />
              <Button
                label="Remover"
                ruby
                xs
                :is-loading="isDeleting"
                @click="deleteSchedule(schedule.id)"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Formulário de criação/edição -->
      <div
        v-if="isFormOpen"
        class="rounded-xl border border-n-weak p-4 flex flex-col gap-4 mt-4"
      >
        <span class="text-sm font-medium text-n-slate-12">
          {{ editingId ? 'Editar agenda' : 'Nova agenda' }}
        </span>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input
            v-model="form.name"
            label="Nome"
            placeholder="Ex.: Reuniões de vendas"
          />

          <label class="flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12">Funil</span>
            <select v-model="form.funnel_id" :class="fieldClass">
              <option value="">Selecione…</option>
              <option
                v-for="opt in funnelOptions"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </select>
          </label>

          <label class="flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12"
              >Fuso horário</span
            >
            <select v-model="form.timezone" :class="fieldClass">
              <option v-for="tz in TIMEZONES" :key="tz.value" :value="tz.value">
                {{ tz.label }}
              </option>
            </select>
          </label>

          <Input
            v-model="form.slot_duration_minutes"
            type="number"
            min="1"
            max="480"
            label="Duração do atendimento (minutos)"
          />

          <Input
            v-model="form.slot_interval_minutes"
            type="number"
            min="1"
            max="480"
            label="Intervalo da grade (minutos)"
            placeholder="Igual à duração se vazio"
          />
        </div>
        <span class="text-xs text-n-slate-11 -mt-2">
          Duração = quanto o atendimento ocupa. Intervalo = a cada quantos
          minutos um novo início pode ser oferecido (ex.: 40 min de consulta a
          cada 20 min, últimos inícios 19:20/16:20 com fim 20:00/17:00). Deixe o
          intervalo vazio para manter grade = duração.
        </span>

        <label class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            Quando usar esta agenda
          </span>
          <textarea
            v-model="form.when_to_use"
            rows="3"
            :class="fieldClass"
            placeholder="Ex.: Use esta agenda para reuniões de demonstração com leads qualificados."
          />
          <span class="text-xs text-n-slate-11">
            Essa descrição ajuda a IA a escolher a agenda certa.
          </span>
        </label>

        <div class="flex flex-col gap-2">
          <label class="flex items-center gap-2 text-sm text-n-slate-12">
            <input v-model="form.enabled" type="checkbox" />
            Agenda ativa
          </label>
          <label class="flex items-center gap-2 text-sm text-n-slate-12">
            <input v-model="form.block_double_booking" type="checkbox" />
            Bloquear 2 agendamentos no mesmo horário
          </label>
        </div>

        <label class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            Etiqueta ao agendar (opcional)
          </span>
          <select v-model="form.label" :class="fieldClass">
            <option value="">Nenhuma</option>
            <option
              v-for="opt in labelOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </select>
          <span class="text-xs text-n-slate-11">
            Quando a IA agendar por esta agenda, aplica esta etiqueta na
            conversa. Crie etiquetas em Configurações → Etiquetas.
          </span>
        </label>

        <!-- Grade de horários (1+ janelas por dia; ex. manhã + tarde) -->
        <div class="flex flex-col gap-2">
          <span class="text-sm font-medium text-n-slate-12">
            Horários de atendimento
          </span>
          <p class="text-xs text-n-slate-11">
            {{ splitHoursHelp }}
          </p>
          <div class="flex flex-col gap-3">
            <div
              v-for="day in DAYS"
              :key="day.key"
              class="flex flex-col gap-2 sm:flex-row sm:items-start"
            >
              <label
                class="flex items-center gap-2 text-sm text-n-slate-12 w-32 shrink-0 pt-2"
              >
                <input
                  v-model="form.business_hours[day.key].enabled"
                  type="checkbox"
                />
                {{ day.label }}
              </label>
              <div
                v-if="form.business_hours[day.key].enabled"
                class="flex flex-col gap-2 flex-1"
              >
                <div
                  v-for="(window, wIndex) in form.business_hours[day.key]
                    .windows"
                  :key="`${day.key}-${wIndex}`"
                  class="flex items-center gap-2 flex-wrap"
                >
                  <input
                    v-model="window.start"
                    type="time"
                    :class="fieldClass"
                    class="!w-32"
                    @change="syncDayBounds(day.key)"
                  />
                  <span class="text-sm text-n-slate-11">até</span>
                  <input
                    v-model="window.end"
                    type="time"
                    :class="fieldClass"
                    class="!w-32"
                    @change="syncDayBounds(day.key)"
                  />
                  <Button
                    v-if="form.business_hours[day.key].windows.length > 1"
                    icon="i-ri-close-line"
                    :aria-label="`Remover período de ${day.label}`"
                    :title="`Remover período de ${day.label}`"
                    slate
                    ghost
                    xs
                    @click="removeWindow(day.key, wIndex)"
                  />
                </div>
                <button
                  type="button"
                  class="text-xs text-n-brand self-start disabled:opacity-50"
                  :disabled="form.business_hours[day.key].windows.length >= 3"
                  @click="addWindow(day.key)"
                >
                  {{ addPeriodLabel }}
                </button>
              </div>
              <span v-else class="text-xs text-n-slate-11 pt-2">
                {{ closedLabel }}
              </span>
            </div>
          </div>
        </div>

        <div class="flex items-center justify-end gap-2">
          <Button
            label="Cancelar"
            slate
            ghost
            sm
            :disabled="isSaving"
            @click="closeForm"
          />
          <Button
            :label="editingId ? 'Salvar alterações' : 'Criar agenda'"
            icon="i-ri-check-line"
            sm
            :is-loading="isSaving"
            @click="saveSchedule"
          />
        </div>
      </div>
    </template>
  </PageLayout>
</template>
